import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Language } from '@/types';
import { supabase } from '@/lib/supabase';

interface LogoutPageProps {
  lang?: Language;
}

export const LogoutPage: React.FC<LogoutPageProps> = ({ lang = 'en' }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTarget = searchParams.get('redirect') || '/nova-workspace';
  const isArabic = lang === 'ar';

  useEffect(() => {
    let isCancelled = false;

    const executeLogoutFlow = async () => {
      const startTime = Date.now();

      try {
        // 1. Purge local cache and session keys
        const keysToRemove = [
          'nova_active_chat_id',
          'nova_session_cache_v1',
          'nova_guest_conversations_v1',
          'vexl_token_cache',
          'vexl_active_layers',
          'nova_user_preferences',
          'nova_mode_storage',
        ];

        keysToRemove.forEach((key) => {
          try {
            localStorage.removeItem(key);
          } catch {
            // Ignore
          }
        });

        try {
          const allKeys = Object.keys(localStorage);
          allKeys.forEach((key) => {
            if (key.startsWith('layer_store_') || key.startsWith('vexl_cache_')) {
              localStorage.removeItem(key);
            }
          });
        } catch {
          // Ignore
        }

        try {
          sessionStorage.clear();
        } catch {
          // Ignore
        }

        // 2. Revoke Supabase session
        const signOutPromise = supabase.auth.signOut();
        const timeoutPromise = new Promise((resolve) => setTimeout(resolve, 2000));

        await Promise.race([signOutPromise, timeoutPromise]);
        if (isCancelled) return;

        // 3. Minimum display time for natural pacing
        const elapsed = Date.now() - startTime;
        const minDisplayTime = 800;
        if (elapsed < minDisplayTime) {
          await new Promise((res) => setTimeout(res, minDisplayTime - elapsed));
        }

        if (!isCancelled) {
          navigate(redirectTarget, { replace: true });
        }
      } catch (error) {
        console.error('[VEXL NOVA] Error during logout:', error);
        if (!isCancelled) {
          navigate(redirectTarget, { replace: true });
        }
      }
    };

    executeLogoutFlow();

    return () => {
      isCancelled = true;
    };
  }, [navigate, redirectTarget]);

  return (
    <div
      dir={isArabic ? 'rtl' : 'ltr'}
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-[#09090b] px-6 select-none"
    >
      <div className="flex flex-col items-center justify-center text-center">
        <p className="text-[15px] font-medium text-slate-100 tracking-normal leading-relaxed">
          {isArabic ? 'تسجيل الخروج من الحساب' : 'Logging out of all accounts'}
        </p>
        <p className="text-[13px] font-normal text-slate-400 mt-1">
          {isArabic ? 'برجاء الانتظار' : 'Please wait'}
        </p>
      </div>
    </div>
  );
};

export default LogoutPage;


